import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { isEmptyObject, hasNoQuotes } from '../../utils/apiHelpers';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { useLocation } from 'react-router-dom';

import MobileProviderTileComponent from '../../components/ProviderTile/MobileProviderTileComponent_BusinessInsuranceFlows';
import ProviderTileComponent from '../../components/ProviderTile/ProviderTileComponent_BusinessInsuranceFlows';

import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import ResultsHeader from '../../components/ResultHeader/ResultHeaderComponent';
import MobileResultsHeader from '../../components/ResultHeader/MobileResultHeaderComponent';
import { useMobileContext } from '../../contexts/MobileContext';

import { getBusinessInsuranceOffers } from '../../api/apiServices';
import { withRouter } from '../../utils/withRouter';
import TakeMeHomeButton from '../../components/TakeMeHomeButton/TakeMeHomeButton';

import gsap from 'gsap';

// Fireworks (match Quote page)
import lottie from 'lottie-web';
import fireworksData from '../../assets/lottie/fireworks.json';
import { getCrossSellQuotesForResults } from '../../utils/crossSellResultsHelper';
import {
  markProductChosen,
  unmarkProductChosen,
  isProductChosen,
  getLandingPathForFallbackKey,
} from '../../utils/carouselQuoteHelpers';
import { pushQuoteResultsView } from '../../utils/dataLayer';

/* ---------------------------------------
   Fireworks overlay
   - Burst from BOTH sides of card top
   - Render BEHIND the ProviderTile
----------------------------------------*/
const FireworksFromTop = ({ playSignal, isMobile }) => {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const leftLottieRef = useRef(null);
  const rightLottieRef = useRef(null);

  useEffect(() => {
    if (leftRef.current) {
      leftLottieRef.current = lottie.loadAnimation({
        container: leftRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    if (rightRef.current) {
      rightLottieRef.current = lottie.loadAnimation({
        container: rightRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    return () => {
      leftLottieRef.current?.destroy();
      rightLottieRef.current?.destroy();
      leftLottieRef.current = null;
      rightLottieRef.current = null;
    };
  }, []);

  useEffect(() => {
    const playForDurationMs = (instance, desiredMs = 2500) => {
      if (!instance) return;
      const nativeSecs =
        typeof instance.getDuration === 'function'
          ? instance.getDuration(false)
          : null;
      const speed =
        nativeSecs && nativeSecs > 0 ? nativeSecs / (desiredMs / 1000) : 1;
      instance.setSpeed(speed);
      instance.goToAndPlay(0);
    };
    playForDurationMs(leftLottieRef.current, 3500);
    playForDurationMs(rightLottieRef.current, 3500);
  }, [playSignal]);

  function getDeviceScreenSize() {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return { width: window.screen.width, height: window.screen.height };
  }

  const { width } = getDeviceScreenSize();
  const FW_SIZE = isMobile ? Math.max(320, Math.round(width * 0.7)) : 520;
  const HALF = FW_SIZE / 2;

  return (
    <>
      <Box
        ref={leftRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          left: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(-35%) rotate(-35deg)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        ref={rightRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          right: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(35%) rotate(35deg)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    </>
  );
};

function BusinessInsuranceResultPage(props) {
  const { navigate, isMobile, isTablet } = props;

  const INITIAL_TILE_APPEAR_MS = 300; // non-top-3 tiles
  const REVEAL_STAGGER_MS = 500; // top-3 tiles
  const BASE_REVEAL_DELAY_MS = 800;

  const { category, product, formValues } = props.location?.state || {};
  const isMobileOrTablet = isMobile || isTablet;

  const bestDealRef = useRef(null);
  const introPanelRef = useRef(null);

  // States
  const [loading, setLoading] = useState(
    !sessionStorage.getItem('hippo_reveal_done')
  );
  const [fetching, setFetching] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // ✅ In this scenario there is NEVER a premium / “calculation”
  // We keep this flag for the tile, but we set it to true once data exists.
  const [bestDealContentReady, setBestDealContentReady] = useState(
    !!sessionStorage.getItem('hippo_reveal_done')
  );

  const [showTeaseModal, setShowTeaseModal] = useState(false);
  const [allowFinalTile, setAllowFinalTile] = useState(false);
  const [hideTop3Permanently, setHideTop3Permanently] = useState(false);

  const [quotes, setQuotes] = useState([]);
  const [headerText, setHeaderText] = useState('');

  const [showIntroPanel, setShowIntroPanel] = useState(
    !props.location?.state?.fromCrossSell
  );
  const [introDelayDone, setIntroDelayDone] = useState(
    !!props.location?.state?.fromCrossSell
  );

  const [quotesForAnimation, setQuotesForAnimation] = useState([]);
  const [visibleQuotes, setVisibleQuotes] = useState(0);

  const scaledOnceRef = useRef(new Set());

  // Fireworks control
  const [fireworksSignal, setFireworksSignal] = useState(0);
  const [fireworksArmed, setFireworksArmed] = useState(false);
  const hasSeenRevealRef = useRef(
    sessionStorage.getItem('hippo_reveal_done') === '1'
  );

  // -------------------------
  // Fetch quotes
  // -------------------------
  useEffect(() => {
    if (isProductChosen('latestBusinessInsuranceQuotes')) {
      navigate(getLandingPathForFallbackKey('latestBusinessInsuranceQuotes'), {
        replace: true,
      });
      return;
    }
    const fetchQuotes = async () => {
      const crossSellQuotes = getCrossSellQuotesForResults(
        props.location?.state
      );
      if (!crossSellQuotes && isEmptyObject(formValues)) {
        setFetching(false);
        return;
      }

      setFetching(true);

      const quotesFromAPI =
        crossSellQuotes ??
        (await getBusinessInsuranceOffers(formValues).catch(() => []));

      if (hasNoQuotes(quotesFromAPI)) {
        setInitialLoadDone(true);
        setFetching(false);
        return;
      }

      // ✅ API ORDER IS SOURCE OF TRUTH:
      //  - index 0 = best deal
      //  - indexes 0..2 = top 3
      const bestId =
        quotesFromAPI[0]?.QuotePK ?? quotesFromAPI[0]?.BrandId ?? 0;

      const top3Ids = new Set(
        quotesFromAPI
          .slice(0, Math.min(3, quotesFromAPI.length))
          .map((q) => q.QuotePK ?? q.BrandId)
      );

      const updatedQuotes = quotesFromAPI.map((quote, index) => {
        const id = quote.QuotePK ?? quote.BrandId ?? index;
        const isBestDeal = id === bestId;
        const isTop3 = top3Ids.has(id);

        // ✅ No premium scenario: no avg/improvement chips
        const tags = [];

        return {
          ...quote,
          _apiIndex: index, // ✅ preserve API order deterministically
          _rank: index + 1, // optional, but consistent with API order
          bestDeal: isBestDeal,
          isTop3,
          RetailChips: tags,
        };
      });

      setQuotes(updatedQuotes);
      pushQuoteResultsView({ product });

      localStorage.setItem(
        'latestBusinessInsuranceQuotes',
        JSON.stringify(updatedQuotes)
      );
      unmarkProductChosen('latestBusinessInsuranceQuotes');

      setHeaderText(
        `You have <b>${quotesFromAPI.length} great offers</b> from leading SA insurers.`
      );

      // ✅ Content is "ready" immediately (no premium calculations)
      setBestDealContentReady(true);

      setInitialLoadDone(false); // reveal flow will finish it
      setFetching(false);
    };

    fetchQuotes();
  }, [formValues]);

  // -------------------------
  // Animate in all except top 3, then show modal (if >5 total) or auto-reveal top 3 (if <=5)
  // -------------------------
  useEffect(() => {
    if (
      fetching ||
      initialLoadDone ||
      !introDelayDone ||
      quotes.length === 0 ||
      quotesForAnimation.length > 0
    ) {
      return;
    }

    const nonTop3 = quotes.filter((q) => !q.isTop3);

    if (nonTop3.length > 0) {
      // ✅ No premium scenario: deterministic reveal = reverse API order (end → start)
      const animationOrder = [...nonTop3].sort(
        (a, b) => b._apiIndex - a._apiIndex
      );

      let delay = 0;
      const step = INITIAL_TILE_APPEAR_MS;
      const firstGap = Math.round(step * 3);

      animationOrder.forEach((quote, index) => {
        setTimeout(() => {
          setQuotesForAnimation((prev) => [quote, ...prev]);

          if (index === animationOrder.length - 1) {
            if (quotes.length > 5) {
              if (hasSeenRevealRef.current) {
                setTimeout(() => setAllowFinalTile(true), 600);
              } else {
                sessionStorage.setItem('hippo_reveal_done', '1');
                setTimeout(() => setShowTeaseModal(true), 1000);
              }
            } else {
              setTimeout(() => setAllowFinalTile(true), 600);
            }
          }
        }, delay);

        delay += index === 0 ? firstGap : step;
      });
    } else {
      // total <=3 case
      setTimeout(() => setAllowFinalTile(true), 400);
    }
  }, [fetching, initialLoadDone, quotes, quotesForAnimation, introDelayDone]);

  // -------------------------
  // Reveal top 3 in sequence (#3 -> #2 -> #1 best)
  // -------------------------
  useEffect(() => {
    if (!allowFinalTile || quotes.length === 0) return;

    const top3AscendingApi = [...quotes]
      .filter((q) => q.isTop3)
      .sort((a, b) => a._apiIndex - b._apiIndex); // [best(#1), #2, #3]

    const revealOrder = [
      top3AscendingApi[2],
      top3AscendingApi[1],
      top3AscendingApi[0],
    ].filter(Boolean);

    if (revealOrder.length === 0) {
      setInitialLoadDone(true);
      return;
    }

    revealOrder.forEach((q, idx) => {
      setTimeout(
        () => {
          setQuotesForAnimation((prev) => [q, ...prev]);

          // ✅ When best deal is revealed, finalize
          if (q?.bestDeal && bestDealRef.current && !initialLoadDone) {
            setTimeout(() => {
              setInitialLoadDone(true);
            }, 300);
          }
        },
        BASE_REVEAL_DELAY_MS + idx * REVEAL_STAGGER_MS
      );
    });
  }, [allowFinalTile, quotes, initialLoadDone]);

  useEffect(() => {
    if (!fetching && !showIntroPanel) {
      setTimeout(() => setIntroDelayDone(true), 2000);
    }
  }, [fetching, showIntroPanel]);

  const ProviderTile = isMobileOrTablet
    ? MobileProviderTileComponent
    : ProviderTileComponent;

  const ResultHeaderComponent = isMobileOrTablet
    ? MobileResultsHeader
    : ResultsHeader;

  useEffect(() => {
    if (hasSeenRevealRef.current) return;
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Finalize initial load after best is revealed (kept for timing polish)
  useEffect(() => {
    if (!loading && bestDealRef.current && allowFinalTile && !initialLoadDone) {
      setTimeout(() => {
        setInitialLoadDone(true);
      }, 1200);
    }
  }, [loading, allowFinalTile, initialLoadDone]);

  // ✅ No-premium scenario: sorting should just keep API order.
  // Keep this state so header component doesn't break, but only provide a single option.
  const [sortOption, setSortOption] = useState('recommended');

  const sortOptions = [];

  const sortedQuotes = useMemo(() => {
    return [...quotes].sort((a, b) => a._apiIndex - b._apiIndex);
  }, [quotes]);

  const handleModalButtonClick = (id) => {
    if (id === 'keep-searching') {
      setShowTeaseModal(false);
      setAllowFinalTile(true);
    } else if (id === 'stop-searching') {
      setShowTeaseModal(false);
      setHideTop3Permanently(true);
      setInitialLoadDone(true);
    }
  };

  const withheldCount = Math.min(3, quotes.length);
  const shownSoFar = Math.max(quotes.length - withheldCount, 0);

  const modalData = {
    title: `We found ${shownSoFar} so far`,
    content: 'But there might be an even better deal. Want us to keep looking?',
    type: 'found-deals',
    buttons: [
      {
        index: 0,
        id: 'keep-searching',
        type: 'primary-pink',
        text: 'Yes, find me the best deal',
      },
      {
        index: 1,
        id: 'stop-searching',
        type: 'secondary-gray',
        text: 'No, Stop Searching',
      },
    ],
  };

  const quotesToRender = initialLoadDone ? sortedQuotes : quotesForAnimation;

  // Only show "No Quotes..." once the reveal flow is finished
  const showNoMatches =
    !fetching && initialLoadDone && quotesToRender.length === 0;

  let summaryText = '';
  if (showNoMatches) summaryText = 'No Quotes Match Your Criteria';

  // Progress caps at "all except 3" until user says Yes, or at full set if user says No
  const progress = useMemo(() => {
    const total = quotes.length;

    const MIN = 0; // 5% baseline
    const PHASE1_MAX = 0.7; // cap before revealing top 3
    const PHASE2_MAX = 0.95; // cap while revealing top 3

    // While fetching, show a little progress so it never looks empty
    if (fetching) return MIN;
    if (total === 0) return 1;

    const withheld = Math.min(3, total);
    const nonTop3Target = Math.max(total - withheld, 1);

    // How many of the non-top-3 have we shown so far?
    const phase1Ratio = Math.min(visibleQuotes / nonTop3Target, 1);

    // If user said "No", we never reveal top 3 → finish at 100% when done
    if (hideTop3Permanently) {
      const ratio = Math.min(visibleQuotes / nonTop3Target, 1);
      const scaled = MIN + (1 - MIN) * ratio;
      return Math.min(scaled, initialLoadDone ? 1 : PHASE2_MAX);
    }

    if (!allowFinalTile) {
      return Math.max(
        MIN,
        Math.min(MIN + (PHASE1_MAX - MIN) * phase1Ratio, PHASE1_MAX)
      );
    }

    // Phase 2: revealing top 3; compute progress over total quotes
    const totalTargetVisible = total;
    const phase2Ratio = Math.min(
      visibleQuotes / Math.max(totalTargetVisible, 1),
      1
    );

    // Map from PHASE1_MAX → PHASE2_MAX as the top 3 come in
    const scaledPhase2 = PHASE1_MAX + (PHASE2_MAX - PHASE1_MAX) * phase2Ratio;

    // Only hit 100% when initialLoadDone is true (best revealed / finished)
    return initialLoadDone ? 1 : Math.min(scaledPhase2, PHASE2_MAX);
  }, [
    fetching,
    visibleQuotes,
    quotes,
    initialLoadDone,
    hideTop3Permanently,
    allowFinalTile,
  ]);

  useEffect(() => {
    if (showIntroPanel && introPanelRef.current) {
      const elements = introPanelRef.current.querySelectorAll('.stagger-in');

      if (elements.length > 0) {
        gsap.set(elements, { opacity: 0, y: 30 });

        setTimeout(() => {
          gsap.to(elements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.1,
            overwrite: true,
          });
        }, 30);
      }
    }
  }, [showIntroPanel]);

  /* ------------------------------
     GSAP helpers
  -------------------------------*/

  const timelinesRef = useRef(new Map());

  const playEntrance = (el, { isBestDeal }) => {
    if (!el) return;
    const motionDiv = el.closest('[data-tile-motion]');

    const prev = timelinesRef.current.get(el.id);
    if (prev) prev.kill();

    const bounceSeq = (target) =>
      gsap
        .timeline()
        .set(
          target,
          { transformOrigin: '50% 50%', rotateZ: 0, scale: 0.75, opacity: 1 },
          0
        )
        .to(target, { scale: 1.06, duration: 0.4, ease: 'power3.out' }, 0)
        .to(
          target,
          { scale: 1.0, duration: 1.0, ease: 'elastic.out(1, 0.75)' },
          '>-0.02'
        );

    const tiltSeq = (target) =>
      gsap
        .timeline({ defaults: { ease: 'power2.inOut' } })
        .to(
          target,
          {
            duration: 1.2,
            rotateZ: -4,
            scale: 1.15,
            ease: 'power1.out',
          },
          0
        )
        .to(
          target,
          {
            duration: 1.5,
            rotateZ: 0,
            scale: 1.0,
            ease: 'elastic.out(1.5, 0.4)',
          },
          '>-0.02'
        );

    const tl = gsap.timeline();
    tl.add(bounceSeq(el), 0);
    if (isBestDeal && !hasSeenRevealRef.current) tl.add(tiltSeq(el), '>-0.90');

    if (isBestDeal) {
      tl.add(
        () => {
          const shouldReveal = !hasSeenRevealRef.current;
          if (shouldReveal) {
            setFireworksArmed(true);
            hasSeenRevealRef.current = true;
            sessionStorage.setItem('hippo_reveal_done', '1');
          }
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setBestDealContentReady(true);
              if (shouldReveal) setFireworksSignal((s) => s + 1);
              setInitialLoadDone(true);
            });
          });
        },
        hasSeenRevealRef.current ? '<' : '>-1.7'
      );
    }

    tl.add(() => {
      if (motionDiv) gsap.set(motionDiv, { zIndex: 'auto' });
    });

    timelinesRef.current.set(el.id, tl);
    return tl;
  };

  const handleSelectQuote = useCallback(
    (e, { data }) => {
      data.businessAcceptQuote = true;
      data.FriendlyName = data.BrandName;
      markProductChosen('latestBusinessInsuranceQuotes');
      navigate('/quoteSelection', {
        replace: true,
        state: { ...data, _product: product },
      });
    },
    [navigate, product]
  );

  const handleBuyOnline = useCallback(
    (e, { data }) => {
      // same destination for now (you can change later to '/buyOnline')
      data.businessAcceptQuote = true;
      data.FriendlyName = data.BrandName;
      markProductChosen('latestBusinessInsuranceQuotes');
      navigate('/quoteSelection', {
        replace: true,
        state: { ...data, _product: product },
      });
    },
    [navigate, product]
  );

  const { pathname } = useLocation();
  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          mt: isMobile ? '0px' : '0.75rem',
          overflowX: 'clip',
        }}
      >
        {showIntroPanel && (
          <Box
            ref={introPanelRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: isTablet ? '24px' : isMobileOrTablet ? '0px' : '32px',
            }}
          >
            <img
              src={HippoAvatar}
              alt="Hippo Avatar"
              style={{
                width: '124px',
                height: '124px',
                objectFit: 'contain',
                margin: isTablet
                  ? '40px auto 0px'
                  : isMobile
                    ? '48px auto 0px'
                    : '0 auto 24px',
              }}
            />
            <Typography
              className="stagger-in"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: isMobileOrTablet ? '1.25rem' : '1.5rem',
                lineHeight: isMobileOrTablet ? '1.5rem' : '1.75rem',
                letterSpacing: '-0.03em',
                fontWeight: 400,
                color: '#162F3B',
                mb: isMobileOrTablet ? 2 : 4,
              }}
            >
              OK. We&rsquo;ve got what we need.
              <br />
              Ready to get your quotes?
            </Typography>

            <Button
              className="stagger-in"
              onClick={() => {
                setShowIntroPanel(false);
                if (!fetching) {
                  setTimeout(() => setIntroDelayDone(true), 2000);
                } else {
                  setIntroDelayDone(true);
                }
              }}
              sx={{
                backgroundColor: '#FF0074',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                borderRadius: '18px',
                padding: isMobileOrTablet ? '12px 24px' : '12px 32px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#e60073',
                },
              }}
            >
              Yes
            </Button>
          </Box>
        )}

        {!showIntroPanel && (
          <Box sx={{ width: '100%', maxWidth: '1376px', margin: '0 auto' }}>
            <ResultHeaderComponent
              headerText={
                quotes.length === 0 ? 'Well, this is awkward...' : headerText
              }
              loadingText="Talking to insurers"
              initialLoadDone={initialLoadDone}
              sortOption={sortOption}
              setSortOption={setSortOption}
              progress={progress}
              sortOptions={sortOptions}
            />

            {introDelayDone && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '32px',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    {/* Center Panel – Tiles */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        mb: isMobileOrTablet ? 'calc(48px + 24px)' : '32px',
                      }}
                    >
                      {fetching ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        />
                      ) : quotes.length === 0 ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        >
                          We searched all our partners, but couldn&rsquo;t find
                          a suitable offer for you right now.
                          <TakeMeHomeButton />
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              width: '100%',
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              fontSize: '1rem',
                              lineHeight: '1.75rem',
                              color: '#1E354E',
                              px: isMobileOrTablet ? '16px' : '0',
                              display: { xs: 'block', md: 'flex' },
                              justifyContent: { md: 'center' },
                            }}
                          >
                            <Box
                              sx={{ width: { md: '900px' }, textAlign: 'left' }}
                            >
                              {summaryText}
                            </Box>
                          </Box>

                          <AnimatePresence mode="popLayout" initial={false}>
                            {quotesToRender.map((quote) => {
                              const isBestDeal = quote.bestDeal;
                              const tileId = `tile-${quote.QuotePK ?? quote.BrandId ?? quote._apiIndex}`;

                              return (
                                <motion.div
                                  data-tile-motion
                                  layout="position"
                                  key={tileId}
                                  transition={{
                                    layout: {
                                      type: 'spring',
                                      stiffness: 520,
                                      damping: 42,
                                      mass: 0.9,
                                    },
                                    opacity: {
                                      duration: 0.28,
                                      ease: 'easeOut',
                                    },
                                    y: { duration: 0.28, ease: 'easeOut' },
                                  }}
                                  /* Enter AFTER layout pop finishes */
                                  initial={{ opacity: 0, y: -14 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 18 }}
                                  style={{
                                    position: 'relative',
                                    willChange: 'transform, opacity',
                                    zIndex: isBestDeal ? 2500 : 'auto',
                                  }}
                                  onAnimationStart={() => {
                                    // run once per tile on first appearance only
                                    if (!scaledOnceRef.current.has(tileId)) {
                                      const el =
                                        document.getElementById(tileId);
                                      if (el) {
                                        playEntrance(el, { isBestDeal });
                                        scaledOnceRef.current.add(tileId);
                                      }
                                    }
                                  }}
                                  onAnimationComplete={() => {
                                    setVisibleQuotes((prev) => prev + 1);
                                  }}
                                >
                                  {/* simple wrapper — no logic change */}
                                  {/* Wrapper: keep the whole best-deal stack above side panels */}

                                  <Box
                                    sx={{
                                      width: '100%',
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Box
                                      id={tileId}
                                      sx={{
                                        position: 'relative',
                                        overflow: 'visible',
                                        zIndex: isBestDeal ? 2500 : 1,

                                        width: '100%',
                                        maxWidth: '1200px', // ✅ match your page/container width
                                      }}
                                    >
                                      {/* Fireworks BEHIND the ProviderTile (zIndex:1 vs card zIndex:2) */}
                                      {isBestDeal && fireworksArmed && (
                                        <FireworksFromTop
                                          playSignal={fireworksSignal}
                                          isMobile={isMobileOrTablet}
                                        />
                                      )}

                                      {/* Card sits above fireworks within this wrapper */}
                                      <Box
                                        sx={{ position: 'relative', zIndex: 2 }}
                                      >
                                        <ProviderTile
                                          category={category}
                                          product={product}
                                          industryCategory={
                                            formValues?.industryCategory
                                              ?.BusinessType
                                          }
                                          data={quote}
                                          initialLoadDone={
                                            isBestDeal || initialLoadDone
                                          }
                                          bestDealContentReady={
                                            isBestDeal
                                              ? bestDealContentReady
                                              : true
                                          }
                                          isMobile={isMobileOrTablet}
                                          onSelectQuote={handleSelectQuote}
                                          onBuyOnline={handleBuyOnline}
                                          ref={isBestDeal ? bestDealRef : null}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Modal */}
            {showTeaseModal && modalData && (
              <PopupModalComponent
                open={showTeaseModal}
                onClose={() => {
                  setShowTeaseModal(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                data={modalData}
                scrollToTop
                onAction={handleModalButtonClick}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

/** Wrapper to inject mobile flags (same pattern as QuestionEngine) */
const ResultPageWithMobile = (props) => {
  const { isMobile, isTablet } = useMobileContext();
  return (
    <BusinessInsuranceResultPage
      {...props}
      isMobile={isMobile}
      isTablet={isTablet}
    />
  );
};

export default withRouter(ResultPageWithMobile);
